import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { firstValueFrom, of, pipe } from 'rxjs';
import { catchError, concatMap, finalize, map, switchMap, tap } from 'rxjs/operators';

import { ApplicationService, UserService } from '../services';
import { initialUserState, UserState } from '../models/state/user.state';
import { PageRequest, User, UserCreateUpdateRequest } from '../models';
import { SelectedCustomerStore } from './selected-customer.store';
import { HttpErrorResponse } from '@angular/common/http';

export const CustomerUserStore = signalStore(
  { providedIn: 'root' },
  withProps(() => {
    return { customer: inject(SelectedCustomerStore)?.selectedCustomer };
  }),
  withState<UserState>(initialUserState),
  withMethods(
    (
      store,
      applicationService = inject(ApplicationService),
      userService = inject(UserService),
      toastrService = inject(ToastrService),
      translateService = inject(TranslateService),
    ) => {
      const loadUsers = rxMethod<PageRequest>(
        pipe(
          map((_pageRequest) => {
            const pageRequest = { ..._pageRequest, page: 0, size: applicationService.itemsPerPage() };
            patchState(store, { loading: true, pageRequest });
            return pageRequest;
          }),
          switchMap((pageRequest) =>
            userService.getCustomerUsers(store.customer()?.id, pageRequest).pipe(
              tap((users) => patchState(store, { users })),
              catchError(() => of()), // Prevent rxMethod from crashing
              finalize(() => patchState(store, { loading: false })),
            ),
          ),
        ),
      );
      const loadMoreUsers = rxMethod<void>(
        pipe(
          map(() => {
            const { page, ...restOfPageRequest } = store.pageRequest();
            const pageRequest = { ...restOfPageRequest, page: (page ?? 0) + 1 };
            patchState(store, { pageRequest });
            return pageRequest;
          }),
          concatMap((pageRequest) =>
            userService
              .getCustomerUsers(store.customer()?.id, pageRequest)
              .pipe(tap((users) => patchState(store, { users: [...store.users(), ...users] }))),
          ),
        ),
      );
      const loadUsersMetrics = rxMethod<void>(
        pipe(
          tap(() => patchState(store, { metricsLoading: true })),
          switchMap(() =>
            userService.getCustomerUsersStatistics(store.customer()?.id).pipe(
              tap((metrics) => patchState(store, { metrics })),
              catchError(() => of()), // Prevent rxMethod from crashing
              finalize(() => patchState(store, { metricsLoading: false })),
            ),
          ),
        ),
      );
      const deleteUser = rxMethod<User>(
        pipe(
          switchMap((user) =>
            userService.deleteCustomerUser(user.id, store.customer()?.id).pipe(
              tap(() => {
                patchState(store, {
                  users: [...store.users().filter((_user) => _user.id !== user.id)],
                });
                toastrService.success(translateService.instant('users.alerts.deleteSuccess'));
              }),
              catchError((error) => {
                toastrService.error(
                  translateService.instant(
                    error.status === 403 ? 'common.alerts.permissionDenied' : 'common.alerts.genericError',
                  ),
                );
                return of();
              }),
            ),
          ),
        ),
      );

      return {
        loadUsers,
        loadMoreUsers,
        loadUsersMetrics,
        deleteUser,

        async createUser(userData: UserCreateUpdateRequest): Promise<void> {
          patchState(store, { saving: true });

          try {
            const createdUser = await firstValueFrom(
              userService.createUserAsUser({
                ...userData,
                customerId: store.customer()?.id,
              }),
            );

            patchState(store, {
              users: [...store.users(), createdUser],
            });

            toastrService.success(translateService.instant('users.alerts.createSuccess'));
          } catch (error: HttpErrorResponse | any) {
            let errorMessage = 'common.alerts.genericError';
            if (error.status === 409) {
              errorMessage = 'users.alerts.emailAlreadyUsedError';
            } else if (error.status === 403) {
              errorMessage = 'common.alerts.permissionDenied';
            } else if (error.error.detail.startsWith('SSO users')) {
              errorMessage = 'users.alerts.ssoUsersError';
            }

            toastrService.error(translateService.instant(errorMessage));
          } finally {
            patchState(store, { saving: false });
          }
        },

        async updateUser(id: number, userData: UserCreateUpdateRequest): Promise<void> {
          patchState(store, { saving: true });

          try {
            const updatedUser = await firstValueFrom(
              userService.updateUserAsUser(id, {
                ...userData,
                customerId: store.customer()?.id,
              }),
            );

            patchState(store, {
              users: store.users().map((u) => (u.id === id ? { ...u, ...updatedUser } : u)),
            });

            toastrService.success(translateService.instant('users.alerts.updateSuccess'));
          } catch (error: HttpErrorResponse | any) {
            toastrService.error(
              translateService.instant(
                error.status === 409
                  ? 'users.alerts.emailAlreadyUsedError'
                  : error.status === 403
                    ? 'common.alerts.permissionDenied'
                    : 'common.alerts.genericError',
              ),
            );
          } finally {
            patchState(store, { saving: false });
          }
        },
      };
    },
  ),
);
